import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import {
	CloseOutlined,
	FolderOpenFilled,
	CheckCircleFilled,
	CloseCircleFilled,
	LoadingOutlined,
} from '@ant-design/icons'
import { Button, Empty, Spin } from 'antd'

// Services
import { getNotifications, markNotificationsRead } from 'services/notifications'

// Style
import styles from './index.module.scss'

export default function NotificationModal({ onClose }) {
	const history = useHistory()
	const [loading, setLoading] = useState(false)
	const [list, setList] = useState([])
	const [activeFilter, setActiveFilter] = useState('all')
	const [counts, setCounts] = useState({ all: 0, read: 0, unread: 0 })

	const fetchData = useCallback(async () => {
		setLoading(true)
		try {
			const data = await getNotifications(1, 50, activeFilter)
			setList(data.notificationsList || [])
			if (data.counts) setCounts(data.counts)
		} catch (error) {
			console.error(error)
		} finally {
			setLoading(false)
		}
	}, [activeFilter])

	useEffect(() => {
		fetchData()
	}, [fetchData])

	const markAsReadLogic = async (item) => {
		if (item.viewed) return
		try {
			await markNotificationsRead([item.id])
			setList((prev) =>
				prev.map((n) => (n.id === item.id ? { ...n, viewed: true } : n))
			)
			setCounts((prev) => ({
				...prev,
				read: prev.read + 1,
				unread: Math.max(0, prev.unread - 1),
			}))
		} catch (error) {
			console.error('Erro ao marcar como lida', error)
		}
	}

	const handleItemClick = async (item) => {
		await markAsReadLogic(item)

		if (item.demand_id) {
			history.push(`/demands/${item.demand_id}`)
			onClose()
		}
	}

	const handleStatusIndicatorClick = async (e, item) => {
		e.stopPropagation()
		await markAsReadLogic(item)
	}

	return (
		<div className={styles.modalContainer}>
			<div className={styles.header}>
				<h3>Notificações</h3>
				<CloseOutlined className={styles.closeIcon} onClick={onClose} />
			</div>

			<div className={styles.body}>
				<div className={styles.filters}>
					<div
						className={`${styles.filterItem} ${activeFilter === 'all' ? styles.active : ''}`}
						onClick={() => setActiveFilter('all')}>
						Todos <span>({counts.all.toString().padStart(2, '0')})</span>
					</div>
					<div
						className={`${styles.filterItem} ${activeFilter === 'read' ? styles.active : ''}`}
						onClick={() => setActiveFilter('read')}>
						Visualizados{' '}
						<span>({counts.read.toString().padStart(2, '0')})</span>
					</div>
					<div
						className={`${styles.filterItem} ${activeFilter === 'unread' ? styles.active : ''}`}
						onClick={() => setActiveFilter('unread')}>
						Não visualizados{' '}
						<span>({counts.unread.toString().padStart(2, '0')})</span>
					</div>
				</div>

				{loading && (
					<div className={styles.centerPadding}>
						<Spin indicator={<LoadingOutlined spin />} />
					</div>
				)}

				{!loading && list?.length === 0 && (
					<Empty
						description="Nenhuma notificação"
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				)}

				{!loading && list?.length > 0 && (
					<div className={styles.list}>
						{list.map((item) => (
							<div
								key={item.id}
								className={`${styles.card} ${!item.viewed ? styles.unread : ''}`}
								onClick={() => handleItemClick(item)}>
								<div className={styles.iconBox}>
									<FolderOpenFilled />
									<div className={styles.miniBadge}>
										{item.kind === 'error' ? (
											<CloseCircleFilled style={{ color: '#f5222d' }} />
										) : (
											<CheckCircleFilled style={{ color: '#52c41a' }} />
										)}
									</div>
								</div>
								<div className={styles.content}>
									<div className={styles.topRow}>
										<span className={styles.author}>
											{item.author || 'Sistema'}
										</span>
										<div
											className={`${styles.statusIndicator} ${item.viewed ? styles.filled : styles.empty}`}
											onClick={(e) =>
												handleStatusIndicatorClick(e, item)
											}></div>
									</div>

									<p className={styles.message}>
										{item.demand?.title
											? item.text.replace(item.demand_id, item.demand.title)
											: item.text}
									</p>

									<span className={styles.date}>
										{moment(item.created_at).format(
											'dddd, DD [de] MMMM [de] YYYY [às] HH:mm A'
										)}
									</span>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			<div className={styles.footer}>
				<Button type="link" onClick={onClose} className={styles.btnFechar}>
					Fechar
				</Button>
			</div>
		</div>
	)
}
